"use client";

import RichTextInput from "@/components/ui/RichTextInput";
import ProductSection from "./EditProductSection";
import { ProductClientState } from "@/zod/product.schema";
import { Controller, useFormContext } from "react-hook-form";

interface DescriptionSectionProps {
  currentLang: "en" | "fa";
  isOpen: boolean;
  onToggle: () => void;
  submitAttempted?: boolean;
}

export function DescriptionSection({
  currentLang,
  isOpen,
  onToggle,
  submitAttempted = false,
}: DescriptionSectionProps) {
  const {
    formState: { errors },
    control,
  } = useFormContext<ProductClientState>();

  const invalid =
    !!errors.description_title_en ||
    !!errors.description_text_en ||
    !!errors.description_title_fa ||
    !!errors.description_text_fa;

  const dir = currentLang === "fa" ? "rtl" : "ltr";
  const editorAlign = currentLang === "fa" ? "text-right" : "text-left";

  return (
    <ProductSection
      title="Description"
      faTitle="توضیحات"
      showFa={currentLang === "fa"}
      dir={dir}
      textAlign={currentLang === "fa" ? "justify" : "left"}
      open={isOpen}
      onToggle={onToggle}
      key={currentLang}
      invalid={submitAttempted && invalid}
    >
      <div className="space-y-4">
        <Controller
          key={`title-${currentLang}`}
          control={control}
          name={`description_title_${currentLang}`}
          defaultValue=""
          render={({ field }) => (
            <RichTextInput
              value={field.value ?? ""} // <-- allow legacy HTML/text to render
              onChange={({ json }) => field.onChange(JSON.stringify(json))} // <-- always save JSON string
              placeholder={currentLang === "fa" ? "عنوان توضیحات" : "Description title"}
              className="bg-white rounded-md border border-gray-200"
              editorClassName={editorAlign}
              dir={dir}
              minHeightClassName="min-h-[3rem]"
            />
          )}
        />

        {(errors.description_title_en || errors.description_title_fa) && (
          <p className="text-red-500 text-xs -mt-2">
            {errors.description_title_en?.message ||
              errors.description_title_fa?.message}
          </p>
        )}

        <Controller
          key={`text-${currentLang}`}
          control={control}
          name={`description_text_${currentLang}`}
          defaultValue=""
          render={({ field }) => (
            <RichTextInput
              value={field.value ?? ""} // <-- allow legacy HTML/text to render
              onChange={({ json }) => field.onChange(JSON.stringify(json))} // <-- always save JSON string
              placeholder={currentLang === "fa" ? "متن توضیحات" : "Description text"}
              className="bg-white rounded-md border border-gray-200"
              editorClassName={editorAlign}
              dir={dir}
              minHeightClassName="min-h-[6rem]"
            />
          )}
        />

        {(errors.description_text_en || errors.description_text_fa) && (
          <p className="text-red-500 text-xs -mt-4">
            {errors.description_text_en?.message ||
              errors.description_text_fa?.message}
          </p>
        )}

        <Controller
          control={control}
          name="description_factory"
          defaultValue=""
          render={({ field }) => (
            <RichTextInput
              value={field.value ?? ""} // <-- allow legacy HTML/text to render
              onChange={({ json }) => field.onChange(JSON.stringify(json))} // <-- always save JSON string
              placeholder={currentLang === "fa" ? "توضیحات کارخانه" : "Factory description"}
              className="bg-white rounded-md border border-gray-200"
              editorClassName={editorAlign}
              dir={dir}
              minHeightClassName="min-h-[4rem]"
            />
          )}
        />

        {errors.description_factory && (
          <p className="text-red-500 text-xs -mt-2">
            {errors.description_factory?.message}
          </p>
        )}
      </div>
    </ProductSection>
  );
}
